import {UserHistoryModel} from "../../user-history/models/user-history";
import {UserHistoryItemsView} from "../../user-history/views/user-history-items";
import {autoinject} from "aurelia-dependency-injection";
import {TemplateManager} from "../../shared/helpers/template-manager";
import Marionette from "../../shared/marionette-mustache";
import { AuthService } from "../../authentication/auth-service";
import { AppUser } from "../../authentication/app-user";

const dom = {
    SECTION_LINKS: ".history-section-link",
    HISTORY_REGION: ".history-region",
    PAGER_SECTION: ".history-pager",
    PAGER_BUTTON: ".history-pager .pager-btn",
    PAGE_LABEL: ".history-pager .page-label",
    PAGE_TOTAL: ".history-pager .page-total",
    HISTORY_SIGNIN_OVERRIDE: ".history-override-link",
    HISTORY_LOADER : ".history-loading",
    HISTORY_NAV: ".history-nav",
    MAIL_LINK: ".mail-link"
};

export class UserHistoryView extends Marionette.LayoutView<UserHistoryModel> {

    public template: string;
    private userHistoryItemsRegion: Marionette.Region;

    constructor(private user: AppUser, public model: UserHistoryModel) {
        super({ model: model });
        this.setupModelChangeListeners();
    }

    public onShow() {
        if (this.user.AuthenticatedByIp) {
            this.hideHistoryRegion();
        } else if (this.user.Sku === "free") {
            this.hideHistoryRegion();
            this.$(dom.HISTORY_REGION).append(`<p>User history is only available to subscribed users. Please contact <a class="mail-link" href="mailto:helpdesklondon@vlex.com">helpdesklondon@vlex.com</a> for available options.</p>`);
        } else if (this.user.Sku.toLowerCase() === "open") {
            this.hideHistoryRegion();
            this.$(dom.HISTORY_REGION).append(`<p>User history is not available for your account. Please contact <a class="mail-link" href="mailto:helpdesklondon@vlex.com">helpdesklondon@vlex.com</a> for available options.</p>`);
        } else {
            this.setupSubViews();

            if (this.model.TimeSpan !== "today") {
                this.model.resetDataWithCurrentTimeSpan();
                this.timeSpanChanged();
            }

            this.model.load();
        }
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events["click " + dom.SECTION_LINKS] = (e) => this.sectionLinkClick(e);
        events["click " + dom.PAGER_BUTTON] = (e) => this.pagerButtonClick(e);
        events["click " + dom.HISTORY_SIGNIN_OVERRIDE] = (e) => this.overrideLinkClick(e);
        events["click " + dom.MAIL_LINK] = (e) => this.mailLinkClick(e);
        return events;
    }

    private mailLinkClick(e: JQueryEventObject) {
        e.stopPropagation();
    }

    private hideHistoryRegion() {
        this.model.Items.reset();
        this.$(dom.HISTORY_LOADER).hide();
        this.$(dom.PAGER_SECTION).hide();
        this.$(dom.HISTORY_NAV).hide();
    }

    private setupSubViews() {
        try {
            const userItemsView = new UserHistoryItemsView({ collection: this.model.Items });
            this.userHistoryItemsRegion = this.addRegion("userHistoryItemsRegion", dom.HISTORY_REGION);
            this.userHistoryItemsRegion.show(userItemsView);
            // hide the region until the data is loaded
            this.$(dom.HISTORY_REGION).hide();
        } catch (ex) {
            dev.log("view not ready");
        }
    }

    private setupModelChangeListeners(): void {
        this.listenTo(this.model, "change:TimeSpan", this.timeSpanChanged);
        this.listenTo(this.model, "change:Page", this.pageChanged);
        this.listenTo(this.model, "change:TotalPages", this.totalPagesChanged);
        this.listenTo(this.model, "change:Loading", this.loadingChanged);
    }

    private sectionLinkClick(e: JQueryEventObject): void {
        e.preventDefault();
        e.stopPropagation();

        dev.log("target link clicked");

        const $target = $(e.currentTarget);
        const section = $target.attr("data-section");
        this.model.TimeSpan = section;
    }

    private timeSpanChanged(): void {
        const selectedTimeSpan = this.model.TimeSpan;
        this.$(dom.SECTION_LINKS).removeClass("selected")
            .filter('[data-section="' + selectedTimeSpan + '"]')
            .addClass("selected");
        // hide the pager until the data is loaded
        this.$(dom.PAGER_SECTION).hide();
        // page has probably changed, too. So update the UI
        this.pageChanged();
    }

    private pagerButtonClick(e: JQueryEventObject): void {
        const $target = $(e.currentTarget);
        const direction = $target.attr("data-page");
        const currentPage = this.model.Page;
        if (direction === "left" && currentPage > 1) {
            this.model.Page = (currentPage - 1);
        }
        else if (direction === "right") {
            this.model.Page = (currentPage + 1);
        }
    }

    private toggleDisabledStateOfControl(e: JQuery, disabled: boolean): void {
        e.toggleClass("disabled", disabled).attr("aria-disabled", disabled.toString());
    }

    private pageChanged() {
        const page = this.model.Page;
        const totalPages = this.model.TotalPages;
        this.$(dom.PAGE_LABEL).text(page);
        this.toggleDisabledStateOfControl(this.$(dom.PAGER_BUTTON).filter('[data-page="left"]'), page === 1);
        this.toggleDisabledStateOfControl(this.$(dom.PAGER_BUTTON).filter('[data-page="right"]'), page === totalPages);
    }

    private totalPagesChanged() {
        const page = this.model.Page; // needed for knowing whether to disable right pager or not
        const totalPages = this.model.TotalPages;
        this.$(dom.PAGE_TOTAL).text(totalPages);
        this.toggleDisabledStateOfControl(this.$(dom.PAGER_BUTTON).filter('[data-page="right"]'), page === totalPages);
    }

    private overrideLinkClick(e) {
        e.preventDefault();
        e.stopPropagation();
        const returnUrl = window.location.pathname;
        const signinUrl = `override?return=${returnUrl}`;
        Backbone.history.navigate(signinUrl, { trigger: true });
    }

    private loadingChanged() {
        if (this.model.Loading) {
            this.$(dom.HISTORY_LOADER).show();
            this.$(dom.HISTORY_REGION).hide();
        } else {
            this.$(dom.HISTORY_LOADER).hide();
            this.$(dom.HISTORY_REGION).show();
            this.$(dom.PAGER_SECTION).toggle(this.model.TimeSpan !== "today");
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/user-history/views/user-history.ts